import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import {
  Building2,
  Users,
  Settings,
  BookOpen,
  ClipboardCheck,
  ArrowRight,
  ChevronLeft,
  Lock,
  Unlock,
  AlertCircle,
  ShieldCheck,
  UserX,
  UserCheck,
  Trash2,
  Copy,
  Check,
  RefreshCw,
} from "lucide-react";

export default function SchoolDashboard() {
  const { schoolId } = useParams();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [school, setSchool] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [schoolRes, membersRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/members`, { headers }),
      ]);

      if (!schoolRes.ok) {
        const body = await schoolRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load school");
      }
      const schoolData = await schoolRes.json();
      setSchool(schoolData);

      if (membersRes.ok) {
        const memberData = await membersRes.json();
        setMembers(memberData);

        // Fetch pending share requests only if admin + restricted mode
        if (schoolData.isAdmin && schoolData.sharingMode === "restricted") {
          const srRes = await fetch(
            `${BACKEND_URL}api/schools/${schoolId}/share-requests`,
            { headers }
          );
          if (srRes.ok) {
            const reqs = await srRes.json();
            setPendingCount(reqs.length);
          }
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const copyInviteCode = () => {
    if (!school?.inviteCode) return;
    navigator.clipboard.writeText(school.inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const regenerateCode = async () => {
    if (!confirm("Regenerate invite code? The old code will stop working immediately.")) return;
    setRegenerating(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/regenerate-code`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to regenerate code");
      const { inviteCode } = await res.json();
      setSchool((prev) => ({ ...prev, inviteCode }));
      toast.success("Invite code regenerated.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  const setMemberStatus = async (userId, status) => {
    setActionInProgress(userId + status);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/members/${userId}/status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ status }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to update status");
      }
      setMembers((prev) =>
        prev.map((m) => (String(m.userId) === userId ? { ...m, status } : m))
      );
      toast.success(status === "disabled" ? "Member disabled." : "Member enabled.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const removeMember = async (userId, name) => {
    if (!confirm(`Remove ${name} from this school?`)) return;
    setActionInProgress(userId + "remove");
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/members/${userId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to remove member");
      }
      setMembers((prev) => prev.filter((m) => String(m.userId) !== userId));
      toast.success("Member removed.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleLeave = async () => {
    if (!confirm(`Leave "${school?.name}"? You will lose access to shared classes.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/leave`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to leave school");
      }
      toast.success("You have left the school.");
      navigate("/schools");
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-content">
        <div className="h-5 w-28 bg-muted animate-pulse rounded mb-6" />
        <div className="h-7 w-56 bg-muted animate-pulse rounded mb-2" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded mb-8" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content max-w-lg">
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} className="shrink-0" />
          {error}
        </div>
        <Button variant="outline" size="sm" className="mt-4 gap-2" onClick={() => navigate("/schools")}>
          <ChevronLeft size={14} />
          Back to My Schools
        </Button>
      </div>
    );
  }

  const isAdmin = school?.isAdmin ?? false;

  return (
    <div className="dashboard-content">
      {/* Back link */}
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        My Schools
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Building2 size={18} className="text-primary" />
          </div>
          <div>
            <h1 className="dashboard-heading-sm">
              {school?.name}
            </h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                {school?.sharingMode === "restricted" ? (
                  <><Lock size={10} />Restricted sharing</>
                ) : (
                  <><Unlock size={10} />Free sharing</>
                )}
              </span>
              {isAdmin && (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  <ShieldCheck size={10} />
                  You are admin
                </span>
              )}
            </div>
          </div>
        </div>

        {isAdmin && (
          <Link to={`/schools/${schoolId}/settings`}>
            <Button variant="outline" size="sm" className="gap-2">
              <Settings size={14} />
              Settings
            </Button>
          </Link>
        )}
      </div>

      {/* ── Admin quick-action row ─────────────────────────────── */}
      {isAdmin && (
        <div className="quick-actions-grid mb-8">
          <Link to={`/schools/${schoolId}/classes`} className="quick-action-card">
            <div className="quick-action-icon"><BookOpen size={20} /></div>
            <div>
              <div className="quick-action-title">All Classes</div>
              <div className="quick-action-desc">View all classes in this school</div>
            </div>
            <ArrowRight size={15} className="quick-action-arrow" />
          </Link>

          {school?.sharingMode === "restricted" && (
            <Link to={`/schools/${schoolId}/share-requests`} className="quick-action-card">
              <div className="quick-action-icon" style={{ position: "relative" }}>
                <ClipboardCheck size={20} />
                {pendingCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: -4,
                      right: -4,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background: "hsl(var(--destructive))",
                      color: "hsl(var(--destructive-foreground))",
                      fontSize: 9,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {pendingCount > 9 ? "9+" : pendingCount}
                  </span>
                )}
              </div>
              <div>
                <div className="quick-action-title">Share Requests</div>
                <div className="quick-action-desc">
                  {pendingCount > 0
                    ? `${pendingCount} pending approval${pendingCount !== 1 ? "s" : ""}`
                    : "No pending requests"}
                </div>
              </div>
              <ArrowRight size={15} className="quick-action-arrow" />
            </Link>
          )}
        </div>
      )}

      {/* ── Members section ───────────────────────────────────── */}
      <div className="mb-2 flex items-center justify-between">
        <h2 className="quick-actions-heading" style={{ marginBottom: 0 }}>
          Members ({members.length})
        </h2>
      </div>

      {/* Invite code card - admin only */}
      {isAdmin && school?.inviteCode && (
        <div className="bg-card border border-border rounded-xl px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">Invite code</span>
            <code className="text-sm font-mono tracking-widest bg-muted px-2.5 py-1 rounded-lg select-all">
              {school.inviteCode}
            </code>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={copyInviteCode}>
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-muted-foreground hover:text-foreground text-xs"
              onClick={regenerateCode}
              disabled={regenerating}
            >
              <RefreshCw size={12} className={regenerating ? "animate-spin" : ""} />
              {regenerating ? "..." : "Regenerate"}
            </Button>
          </div>
        </div>
      )}

      {/* Members list */}
      <div className="space-y-2">
        {members.map((member) => {
          const uid = String(member.userId);
          const busy = actionInProgress?.startsWith(uid);

          return (
            <div
              key={uid}
              className="flex items-center justify-between gap-3 bg-card border border-border rounded-xl px-4 py-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                {member.isAdmin && (
                  <ShieldCheck size={14} className="text-primary shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {member.name}
                    {member.isAdmin && (
                      <span className="ml-1.5 text-xs text-primary font-normal">Admin</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{member.email}</div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    member.status === "active"
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {member.status}
                </span>

                {/* Admin controls - only visible to admin, and not on admin's own row */}
                {isAdmin && !member.isAdmin && (
                  <div className="flex gap-1">
                    {member.status === "active" ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                        title="Disable member"
                        disabled={busy}
                        onClick={() => setMemberStatus(uid, "disabled")}
                      >
                        <UserX size={13} />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                        title="Enable member"
                        disabled={busy}
                        onClick={() => setMemberStatus(uid, "active")}
                      >
                        <UserCheck size={13} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      title="Remove member"
                      disabled={busy}
                      onClick={() => removeMember(uid, member.name)}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Non-admin footer ─────────────────────────────────────── */}
      {!isAdmin && (
        <div className="mt-8 space-y-4">
          {/* Settings locked message */}
          <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3.5">
            <Lock size={15} className="text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">
                You are not permitted to change school settings
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Contact your school admin to update sharing settings, domain
                restrictions, or member permissions.
              </p>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
            onClick={handleLeave}
          >
            Leave School
          </Button>
        </div>
      )}
    </div>
  );
}
